import { z } from 'zod';
import { lookSchema } from './look.js';
import type { Look } from './look.js';

/**
 * Friends, presence, private messages, invites, emotes, badges and room likes.
 * Owned by the social feature. Every WebSocket message type starts with
 * `social:`; slower actions (requests, blocks, sending a message) are HTTP
 * under /api/social so they get proper status codes.
 */

/* -------------------------------------------------------------------------- */
/* Limits                                                                     */
/* -------------------------------------------------------------------------- */

export const MAX_FRIENDS = 200;
export const MAX_PENDING_OUTGOING = 50;
export const MAX_DM_LENGTH = 240;
/** Emotes one player may send: one per this many ms. */
export const EMOTE_COOLDOWN_MS = 1500;
/** How long an emote bubble stays above a head. */
export const EMOTE_DURATION_MS = 3000;
/** Minimum gap between two invites from the same player to the same friend. */
export const INVITE_COOLDOWN_MS = 15_000;
export const DM_PAGE_SIZE = 30;
/** Snowballs one player may throw: one per this many ms. */
export const SNOWBALL_COOLDOWN_MS = 4000;
/** How long a hit player stays knocked over. */
export const SNOWBALL_FALL_MS = 2200;

/* -------------------------------------------------------------------------- */
/* Emotes                                                                     */
/* -------------------------------------------------------------------------- */

export const EMOTE_IDS = ['laugh', 'heart', 'cheer', 'sad', 'angry', 'wow', 'sleepy', 'cool', 'kopi', 'sgflag'] as const;
export type EmoteId = (typeof EMOTE_IDS)[number];
export const emoteIdSchema = z.enum(EMOTE_IDS);

export const EMOTES: Readonly<Record<EmoteId, { label: string; shortcut: string }>> = Object.freeze({
  laugh: { label: 'Laugh', shortcut: ':laugh' },
  heart: { label: 'Love', shortcut: ':heart' },
  cheer: { label: 'Cheer', shortcut: ':cheer' },
  sad: { label: 'Sad', shortcut: ':sad' },
  angry: { label: 'Angry', shortcut: ':angry' },
  wow: { label: 'Wow', shortcut: ':wow' },
  sleepy: { label: 'Sleepy', shortcut: ':sleepy' },
  cool: { label: 'Cool', shortcut: ':cool' },
  kopi: { label: 'Kopi time', shortcut: ':cheers' },
  sgflag: { label: 'Majulah!', shortcut: ':sg' },
});

/* -------------------------------------------------------------------------- */
/* Badges                                                                     */
/* -------------------------------------------------------------------------- */

export interface BadgeDef {
  id: string;
  label: string;
  description: string;
  /**
   * 12x12 icon, one string per row, one palette letter per pixel ('.' is
   * transparent). Letters follow the client's icon palette: k ink, w white,
   * c cream, y yellow, b brown, d dark brown, r red, g teal, n blue, s grey,
   * p pink, l light blue.
   */
  icon: readonly string[];
}

const STAR = [
  '.....kk.....',
  '....kyyk....',
  '....kyyk....',
  'kkkkkyykkkkk',
  'kyyyyyyyyyyk',
  '.kyyyyyyyyk.',
  '..kyyyyyyk..',
  '..kyyyyyyk..',
  '.kyyykkyyyk.',
  '.kyyk..kyyk.',
  'kyyk....kyyk',
  'kkk......kkk',
];

/** A trophy icon, recoloured per leaderboard so each one reads as distinct. */
function trophy(fill: string): readonly string[] {
  return [
    '............',
    `.k${fill}${fill}${fill}${fill}${fill}${fill}${fill}k..`,
    `kk${fill}${fill}${fill}${fill}${fill}${fill}${fill}kk.`,
    `k${fill}${fill}${fill}${fill}${fill}${fill}${fill}${fill}${fill}kk`,
    `.k${fill}${fill}${fill}${fill}${fill}${fill}${fill}k..`,
    `..k${fill}${fill}${fill}${fill}${fill}k...`,
    '...kyyyk....',
    '...kyyyk....',
    `..k${fill}${fill}${fill}${fill}${fill}k...`,
    `.k${fill}${fill}${fill}${fill}${fill}${fill}${fill}k..`,
    '.kkkkkkkkk..',
    '............',
  ];
}

/** A crown icon, recoloured per job so each career's top rank reads as distinct. */
function crown(fill: string): readonly string[] {
  return [
    '............',
    '.k.......k..',
    '.kk.....kk..',
    `k${fill}kk...kk${fill}k.`,
    `k${fill}${fill}k.k.k${fill}${fill}k.`,
    `k${fill}${fill}k${fill}k${fill}k${fill}${fill}k.`,
    `k${fill}${fill}${fill}${fill}${fill}${fill}${fill}${fill}${fill}k.`,
    `.k${fill}${fill}${fill}${fill}${fill}${fill}${fill}k..`,
    '.kkkkkkkkk..',
    '..kkkkkkk...',
    '............',
    '............',
  ];
}

/**
 * Live leaderboard badges: held only while a player is actually ranked, so
 * these are awarded and revoked as standings shift (unlike every other badge,
 * which is earned once and kept forever). Job rank badges below are the
 * exception among these two groups: earned once you reach the top rank, kept
 * for good, like the rest of the catalogue.
 */
const RANK_BADGES: readonly BadgeDef[] = [
  { id: 'top10_respects', label: 'Top 10: Respected', description: 'Currently in the top 10 most-respected kakis.', icon: trophy('y') },
  { id: 'top10_kopi_catch', label: 'Top 10: Kopi Catch', description: 'Currently in the top 10 Kopi Catch scores.', icon: trophy('b') },
  { id: 'top10_mrt_dash', label: 'Top 10: MRT Dash', description: 'Currently in the top 10 MRT Dash scores.', icon: trophy('n') },
  { id: 'top10_kueh_stack', label: 'Top 10: Kueh Stack', description: 'Currently in the top 10 Kueh Stack scores.', icon: trophy('p') },
  { id: 'top10_chope', label: 'Top 10: Chope!', description: 'Currently in the top 10 Chope! scores.', icon: trophy('r') },
  { id: 'job_barista', label: 'Cafe Manager', description: 'Reached the top rank as a Kopi Barista.', icon: crown('b') },
  { id: 'job_clerk', label: 'Director', description: 'Reached the top rank as an Office Clerk.', icon: crown('n') },
  { id: 'job_cashier', label: 'Store Manager', description: 'Reached the top rank as a Mall Cashier.', icon: crown('p') },
  { id: 'job_chef', label: 'Hawker Legend', description: 'Reached the top rank as a Hawker Chef.', icon: crown('r') },
];

/** Rank-based badges whose holders change over time and must be re-synced, rather than earned once. */
export const LIVE_RANK_BADGE_IDS: readonly string[] = [
  'top10_respects',
  'top10_kopi_catch',
  'top10_mrt_dash',
  'top10_kueh_stack',
  'top10_chope',
];

export const BADGES: readonly BadgeDef[] = Object.freeze([
  {
    id: 'first_kaki',
    label: 'First Kaki',
    description: 'Made your first friend.',
    icon: [
      '............',
      '.kkk....kkk.',
      'kccck..kccck',
      'kckck..kckck',
      'kccck..kccck',
      '.kkkk..kkkk.',
      '.kggk..knnk.',
      'kggggkknnnnk',
      'kggggkknnnnk',
      'kggggkknnnnk',
      '.kkkk..kkkk.',
      '............',
    ],
  },
  {
    id: 'popular',
    label: 'Popular',
    description: 'Have 10 friends.',
    icon: [
      '............',
      '..kkk..kkk..',
      '.kccckkccck.',
      '.kcccccccck.',
      '..kkkkkkkk..',
      '.kyyyyyyyyk.',
      'kyyyyyyyyyyk',
      'kyykkyykkyyk',
      'kyyyyyyyyyyk',
      '.kyyyyyyyyk.',
      '..kkkkkkkk..',
      '............',
    ],
  },
  {
    id: 'respected',
    label: 'Respected',
    description: 'Received 10 respects.',
    icon: [
      '............',
      '.kkk...kkk..',
      'krrrk.krrrk.',
      'krwrrkrrrrk.',
      'krrrrrrrrrk.',
      'krrrrrrrrrk.',
      '.krrrrrrrk..',
      '..krrrrrk...',
      '...krrrk....',
      '....krk.....',
      '.....k......',
      '............',
    ],
  },
  {
    id: 'well_respected',
    label: 'Well Respected',
    description: 'Received 50 respects.',
    icon: [
      '.kkk...kkk..',
      'kyyyk.kyyyk.',
      'kywyykyyyyk.',
      'kyyyyyyyyyk.',
      'kyyyyyyyyyk.',
      '.kyyyyyyyk..',
      '..kyyyyyk...',
      '...kyyyk....',
      '....kyk.....',
      '.....k......',
      '...kkkkk....',
      '...kdddk....',
    ],
  },
  {
    id: 'room_maker',
    label: 'Room Maker',
    description: 'Built a room of your own.',
    icon: [
      '.....kk.....',
      '....krrk....',
      '...krrrrk...',
      '..krrrrrrk..',
      '.krrrrrrrrk.',
      'kkkkkkkkkkkk',
      '.kcccccccck.',
      '.kcllkkcllk.',
      '.kcllkkcllk.',
      '.kcccddccck.',
      '.kcccddccck.',
      '.kkkkkkkkkk.',
    ],
  },
  {
    id: 'crowd_pleaser',
    label: 'Crowd Pleaser',
    description: 'One of your rooms got 10 likes.',
    icon: [
      '............',
      '....kk......',
      '...kyyk.....',
      '...kyyk.....',
      '...kyyk.....',
      '.kkkyykkkkk.',
      'kyyyyyyyyyk.',
      'kyyyyyyyyyk.',
      'kyyyyyyyyyk.',
      'kyyyyyyyyk..',
      '.kkkkkkkk...',
      '............',
    ],
  },
  {
    id: 'night_owl',
    label: 'Night Owl',
    description: 'Hung out between 1am and 4am (Singapore time).',
    icon: [
      '............',
      '..kk....kk..',
      '.kbbk..kbbk.',
      '.kbbbkkbbbk.',
      'kbwwbbbbwwbk',
      'kbwkbbbbwkbk',
      'kbbbbyybbbbk',
      '.kbbbyybbbk.',
      '.kbcccccbbk.',
      '..kcccccck..',
      '...kkkkkk...',
      '...y....y...',
    ],
  },
  {
    id: 'early_kaki',
    label: 'Early Kaki',
    description: 'Joined Kopi Kaki in its first year, 2026.',
    icon: STAR,
  },
  {
    id: 'chatterbox',
    label: 'Chatterbox',
    description: 'Sent 100 private messages.',
    icon: [
      '............',
      '.kkkkkkkkkk.',
      'kwwwwwwwwwwk',
      'kwkkwkkkwwwk',
      'kwwwwwwwwwwk',
      'kwkkkkwkkwwk',
      'kwwwwwwwwwwk',
      '.kkkwwkkkkk.',
      '...kwk......',
      '...kk.......',
      '............',
      '............',
    ],
  },
  ...RANK_BADGES,
]);

export function badgeDef(id: string): BadgeDef | undefined {
  return BADGES.find((badge) => badge.id === id);
}

/** Most badges a player may feature on their profile at once. */
export const MAX_DISPLAY_BADGES = 5;

/**
 * The badges shown on a profile card: the player's chosen ones, in the order
 * they picked, dropping any that are no longer valid. A player who has never
 * chosen falls back to their most recently earned badges.
 */
export function displayedBadges(earned: readonly BadgeEntry[], selected: readonly string[]): BadgeEntry[] {
  if (selected.length === 0) return earned.slice(-MAX_DISPLAY_BADGES);
  const byId = new Map(earned.map((badge) => [badge.id, badge]));
  return selected
    .map((id) => byId.get(id))
    .filter((badge): badge is BadgeEntry => Boolean(badge))
    .slice(0, MAX_DISPLAY_BADGES);
}

/* -------------------------------------------------------------------------- */
/* Shapes                                                                     */
/* -------------------------------------------------------------------------- */

export type FriendStatus = 'accepted' | 'incoming' | 'outgoing';

export interface FriendEntry {
  id: string;
  displayName: string;
  look: Look;
  status: FriendStatus;
  /** Only meaningful for accepted friends; false for anyone appearing offline. */
  online: boolean;
  roomId: string | null;
  roomName: string | null;
  lastSeenAt: number;
  /** When the request was made or accepted. */
  since: number;
}

export interface BlockedEntry {
  id: string;
  displayName: string;
}

export interface FriendsSnapshot {
  friends: FriendEntry[];
  blocked: BlockedEntry[];
  /** Unread private messages, per friend id. */
  unread: Record<string, number>;
}

export interface DirectMessage {
  id: string;
  fromId: string;
  toId: string;
  body: string;
  at: number;
  readAt: number | null;
}

export interface Conversation {
  friendId: string;
  displayName: string;
  look: Look;
  lastBody: string;
  lastAt: number;
  lastFromMe: boolean;
  unread: number;
}

export interface BadgeEntry {
  id: string;
  earnedAt: number;
}

/* -------------------------------------------------------------------------- */
/* WebSocket messages                                                         */
/* -------------------------------------------------------------------------- */

const idSchema = z.string().min(1).max(64);

export const directMessageSchema = z.object({
  id: z.string(),
  fromId: z.string(),
  toId: z.string(),
  body: z.string(),
  at: z.number(),
  readAt: z.number().nullable(),
});

const personSchema = z.object({ id: z.string(), displayName: z.string(), look: lookSchema });

export const friendEntrySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  look: lookSchema,
  status: z.enum(['accepted', 'incoming', 'outgoing']),
  online: z.boolean(),
  roomId: z.string().nullable(),
  roomName: z.string().nullable(),
  lastSeenAt: z.number(),
  since: z.number(),
});

export const socialClientMessageSchema = z.discriminatedUnion('t', [
  /** Ask for a fresh friends snapshot (sent automatically on connect). */
  z.object({ t: z.literal('social:sync') }),
  /** Show an emoticon above your head to everyone in the room. */
  z.object({ t: z.literal('social:emote'), emote: emoteIdSchema }),
  /** Invite an online friend to the room you are in. */
  z.object({ t: z.literal('social:invite'), friendId: idSchema }),
  /** Hide (or show) your online status from friends for this session. */
  z.object({ t: z.literal('social:appear'), offline: z.boolean() }),
  /** Throw a snowball at another player in the room; they take a tumble. */
  z.object({ t: z.literal('social:snowball'), targetId: idSchema }),
]);

export const socialServerMessageSchema = z.discriminatedUnion('t', [
  /** Something changed; refetch over HTTP if you hold social state. */
  z.object({ t: z.literal('social:changed') }),
  /** Full friends snapshot. */
  z.object({
    t: z.literal('social:friends'),
    friends: z.array(friendEntrySchema),
    blocked: z.array(z.object({ id: z.string(), displayName: z.string() })),
    unread: z.record(z.string(), z.number().int()),
  }),
  /** A friend came online, went offline or changed room. */
  z.object({
    t: z.literal('social:presence'),
    friendId: z.string(),
    online: z.boolean(),
    roomId: z.string().nullable(),
    roomName: z.string().nullable(),
    lastSeenAt: z.number(),
  }),
  z.object({ t: z.literal('social:request'), from: personSchema }),
  z.object({ t: z.literal('social:accepted'), friend: personSchema }),
  z.object({ t: z.literal('social:invite'), from: personSchema, roomId: z.string(), roomName: z.string() }),
  /** A private message, to its recipient (and echoed to the sender's other tabs). */
  z.object({ t: z.literal('social:dm'), message: directMessageSchema, fromName: z.string() }),
  z.object({ t: z.literal('social:emote'), avatarId: z.string(), emote: emoteIdSchema }),
  z.object({ t: z.literal('social:badge'), badgeId: z.string() }),
  /** A snowball flew from `fromId` and landed on `targetId`, who takes a tumble. */
  z.object({ t: z.literal('social:snowball'), fromId: z.string(), targetId: z.string() }),
]);

export type SocialClientMessage = z.infer<typeof socialClientMessageSchema>;
export type SocialServerMessage = z.infer<typeof socialServerMessageSchema>;
